import { formatCurrency } from '@/helper/utils';
import { ProjectDropdownDto } from '@/types/project.types';
import { SiteByProjectIdDto } from '@/types/site.types';
import { SurveyListItem } from '@/types/survey.types';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { surveysetCreationType } from './CreateSurveySet';

type ReviewAndCreateProps = {
  projects: ProjectDropdownDto[];
  sites: SiteByProjectIdDto[];
  surveys: SurveyListItem[];
  isConfirmed: boolean;
  setIsConfirmed: (isConfirmed: boolean) => void;
  showSuccessModal: boolean;
  isEditing?: boolean;
};

const ReviewAndCreate: React.FC<ReviewAndCreateProps> = ({
  projects,
  sites,
  surveys,
  isConfirmed,
  setIsConfirmed,
  showSuccessModal,
  isEditing = false,
}) => {
  const { getValues } = useFormContext<surveysetCreationType>();

  const values = getValues();
  const selectedProject = projects.find((p) => p.id == values.projectId);
  const projectName = selectedProject?.name || 'Not selected';

  const selectedSite = sites.find((s) => s.id === values.siteId);
  const siteName = selectedSite?.name || 'Not selected';
  // TODO: Ask backend team
  // const siteLanguages = selectedSite?.languages?.map((lang: any) => lang.name).join(', ') || 'N/A';

  const activeIds = values.surveyIds.slice(0, values.numberOfSurveys);
  const selectedSurveys = surveys.filter((s) => activeIds.includes(s.id));

  const claimTypeDisplay =
    values.claimType === 'COMPLETE_SURVEY_SET' ? 'Complete Survey Set' : 'Per Survey';

  const submissionRuleMap: Record<string, string> = {
    ONCE: 'Once',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    NO_LIMIT: 'No Limit',
  };

  const submissionRuleDisplay = submissionRuleMap[values.submissionRule!] || '—';

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="mx-auto text-[#621C43]">
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">
          {isEditing ? 'Review & Update' : 'Review & Create Survey-set'}
        </h2>
        <p className="text-base font-medium">
          {isEditing
            ? 'Review all information before updating the Survey-set.'
            : 'Review all information before creating the Survey-set.'}
        </p>
      </div>
      <div className="px-6 py-3 space-y-4">
        {/* Basic Info and Schedule */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Basic Information</h3>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between items-center">
                <span className="font-light">Survey-Set Title</span>
                <span className="font-medium">{values.title || '—'}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-light">Linked Project</span>
                <span className="font-medium">{projectName}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-light">Linked Site</span>
                <span className="font-medium">{siteName}</span>
              </li>
              {/* TODO */}
              {/* <li className='flex'>
              <span className='font-medium min-w-[140px]'>Languages:</span>
              <span className='text-gray-600'>{siteLanguages}</span>
            </li> */}
              <li className="flex justify-between items-center">
                <span className="font-light">Number of Surveys</span>
                <span className="font-medium">{values.numberOfSurveys || 0}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-light">Currency</span>
                <span className="font-medium">{values.currency.toUpperCase() || '—'}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-light">Reward Amount</span>
                <span className="font-medium">
                  {formatCurrency(values.rewardAmount, values.currency) || 0}
                </span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-light">Claim Type</span>
                <span className="font-medium">{claimTypeDisplay}</span>
              </li>
            </ul>
          </div>

          {/* Schedule & Rules */}
          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Schedule & Rules</h3>

            <ul className="text-sm space-y-1">
              <li className="flex justify-between items-center">
                <span className="font-light">Start Date</span>
                <span className="font-medium">{formatDate(values.startDate)}</span>
              </li>

              <li className="flex justify-between items-center">
                <span className="font-light">End Date</span>
                <span className="font-medium">{formatDate(values.endDate)}</span>
              </li>

              <li className="flex justify-between items-center">
                <span className="font-light">Submission Rule</span>
                <span className="font-medium">{submissionRuleDisplay}</span>
              </li>

              <li className="flex justify-between items-center">
                <span className="font-light">Participants Limit</span>
                <span className="font-medium">
                  {values.participantLimit ? <>Max: {values.maxParticipants || '—'}</> : 'No limit'}
                </span>
              </li>

              <li className="flex justify-between items-center">
                <span className="font-light">Status</span>
                <span
                  className={`inline-block px-3 py-0.5 text-xs font-medium rounded-full text-white ${
                    values.status ? 'bg-pink-800' : 'bg-red-800'
                  }`}
                >
                  {values.status ? 'Active' : 'Inactive'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Messages Section */}
        <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
          <h3 className="font-medium text-base mb-2">Message</h3>
          <div className="grid md:grid-cols-2 gap-6 auto-rows-fr">
            <div className="h-full flex flex-col">
              <p className="text-sm font-light text-[#883c4a] mb-1">Greeting Message</p>
              <div className="flex-1 p-2 px-3 rounded-lg text-sm bg-[#4A082D33] text-[#4A082D]">
                {values.greetingMsg || 'Not set'}
              </div>
            </div>

            <div className="h-full flex flex-col">
              <p className="text-sm font-light text-[#883c4a] mb-1">Consent Message</p>
              <div className="flex-1 p-2 px-3 rounded-lg text-sm bg-[#4A082D33] text-[#4A082D]">
                {values.consentMsg || 'Not set'}
              </div>
            </div>

            {/* <div className="h-full flex flex-col">
              <p className="text-sm font-light text-[#883c4a] mb-1">Reward Claim Message</p>
              <div className="flex-1 p-2 px-3 rounded-lg text-sm bg-[#4A082D33] text-[#4A082D]">
                {values.rewardMsg || 'Not set'}
              </div>
            </div> */}
          </div>
        </div>

        {/* Survey List */}
        <div className="">
          <h3 className="text-sm font-semibold text-[#883c4a] mb-1">Surveys List</h3>
          {selectedSurveys.length > 0 ? (
            <div className="flex flex-wrap gap-2 bg-[#4A082D33] p-2 px-3 rounded-xl">
              {selectedSurveys.map((survey, index) => (
                <div
                  key={survey.id}
                  className="p-2 bg-[#883c4a] text-white rounded-xl flex items-center"
                >
                  <span className="text-xs font-bold">{survey.title}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-2 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
              No surveys selected
            </div>
          )}
        </div>

        {/* Validation Warnings */}
        {!showSuccessModal &&
          (!values.title || !values.siteId || values.surveyIds.length === 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                {!values.title && <li>Survey-Set Title is required</li>}
                {!values.siteId && <li>Site selection is required</li>}
                {values.surveyIds.length === 0 && <li>At least one survey must be selected</li>}
              </ul>
            </div>
          )}

        {/* Confirm Section */}
        <div className="border border-[#F2EDEE] bg-[#F2EDEE] rounded-xl p-4">
          <label className="flex items-start gap-2 text-sm text-[#883c4a] ">
            <input
              type="checkbox"
              className="mt-1 accent-[#621C43]"
              checked={isConfirmed}
              onChange={() => setIsConfirmed(!isConfirmed)}
              disabled={!values.title || !values.siteId || values.surveyIds.length === 0}
            />
            <div>
              <span>
                {isEditing
                  ? 'I confirm that all the information provided is accurate and I want to update this Survey-set. '
                  : 'I confirm that all the information provided is accurate and I want to create this Survey-set.'}
              </span>

              <p className="text-xs text-[#883c4a] mt-1">
                {isEditing
                  ? 'This action will update this survey-set.'
                  : 'This action will create a new survey-set.'}
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ReviewAndCreate;
